import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CheckCircle2, Table, ArrowLeft, Clock, ShieldCheck,
  FileText, Download, UserCheck, Info, Sparkles, Key, X
} from 'lucide-react';
import Calculator from '../components/Calculator';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../Firebase';

export const defaultDepositSchemes = [
  {
    id: 'savings-dep',
    name: 'Savings Deposit Account',
    tagline: 'Maintain full liquidity of your cash reserves while earning steady interest yields',
    rate: '4.00% p.a.',
    numericRate: 4.00,
    tenure: 'Continuous',
    minDeposit: '₹500',
    numericMin: 500,
    maxDeposit: 'No Limit',
    features: [
      'Ideal for daily transaction liquidity with zero withdrawal notice times.',
      'Seamless linking to digital apps like GPay, PhonePe, and Net Banking.',
      'Zero balance maintenance charges for students and senior citizens.',
      'Instant passbook printing and monthly e-statements sent via email.'
    ],
    benefits: [
      'High liquidity: Withdraw anytime using debit cards or UPI systems.',
      'Cooperative safety: Monitored regularly under regulatory credit audit rules.',
      'Interest payouts: Credited directly to your account every quarter.'
    ],
    overview: 'Our Savings Deposit Account serves as a foundational credit ledger for members. Perfect for storing emergency funds, monthly earnings, or daily business revenues. We provide access to unified UPI applications, standard branch desks, and secure debit cards to guarantee cash access 24/7.',
    eligibility: [
      'Individual Indian residents (sole or joint accounts).',
      'Minors represented by natural or legal guardians.',
      'Registered co-operative housing societies, local trusts, and clubs.'
    ],
    faqs: [
      { q: 'Is there a penalty for zero transactions?', a: 'No, we do not charge any transaction penalties or dormant account fees.' },
      { q: 'Can I link my account to Google Pay?', a: 'Yes, you can register your debit card on any standard UPI application.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  },
  {
    id: 'fixed-dep',
    name: 'Fixed Deposit (FD)',
    tagline: 'Lock in lump-sum capital at high-interest tiers with quarterly compounding benefits',
    rate: '6.75% - 8.25% p.a.',
    numericRate: 8.25,
    tenure: '7 Days to 10 Years',
    minDeposit: '₹1,000',
    numericMin: 1000,
    maxDeposit: 'No Limit',
    features: [
      'Guaranteed interest returns unaffected by stock market fluctuations.',
      'Quarterly compound calculations to maximize your final maturity yield.',
      'Loan margins allowed up to 90% of accumulated deposit value.',
      'Auto-renewal directives available to extend terms instantly.'
    ],
    benefits: [
      'Market insulation: Locked rates ensure secure maturity planning.',
      'Multiple tenures: Invest for 7 days up to 10 years.',
      'Flexible payouts: Receive interest monthly, quarterly, or at maturity.'
    ],
    overview: 'Sahyadri Fixed Deposits offer premium security and high interest rates for locking in capital. By utilizing a quarterly compounding cycle, your principal builds up significantly over the tenure. Ideal for families saving for long-term goals or businesses managing idle reserve capital.',
    eligibility: [
      'Individual residents, joint co-members, and Hindu Undivided Families (HUFs).',
      'Registered partnerships, sole traders, and private trusts.',
      'Cooperative societies and local educational boards.'
    ],
    faqs: [
      { q: 'What is the penalty for premature closure?', a: 'Premature withdrawals are permitted with a nominal 1.00% interest rate penalty.' },
      { q: 'Can I borrow against my FD receipt?', a: 'Yes, we provide instant loans up to 90% of your deposit value at just +1.5% interest.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  },
  {
    id: 'recurring-dep',
    name: 'Recurring Deposit (RD)',
    tagline: 'Set aside systematic monthly savings to build a large maturity cash corpus',
    rate: '7.00% - 8.00% p.a.',
    numericRate: 8.00,
    tenure: '12 Months to 120 Months',
    minDeposit: '₹500 / month',
    numericMin: 500,
    maxDeposit: 'No Limit',
    features: [
      'Save systematic small installments instead of locking a large lump sum.',
      'Interest rate remains locked throughout your chosen term plan.',
      'Auto-debit mandates from Savings accounts avoid overdue penalties.',
      'Maturity returns compounded quarterly for enhanced payouts.'
    ],
    benefits: [
      'Habit-forming savings: Ideal for salaried professionals.',
      'Flexible intervals: Installments can start as low as ₹500/month.',
      'Collateral option: Avail emergency overdrafts against accumulated RD values.'
    ],
    overview: 'Our Recurring Deposit (RD) helps members design systematic monthly wealth targets. By contributing fixed monthly payments, you gain compound interest yields identical to fixed deposits. It provides an ideal investment pathway for salaried individuals, local vendors, and home planners.',
    eligibility: [
      'Individual co-members, partners, or joint applicants.',
      'Guardians acting on behalf of minor family members.',
      'Local self-help groups (SHGs) and women cooperatives.'
    ],
    faqs: [
      { q: 'What happens if I miss a monthly installment?', a: 'A nominal grace period is given, after which a small late charge applies.' },
      { q: 'Can I change my monthly deposit size?', a: 'No, the monthly contribution size remains fixed throughout the tenure.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  },
  {
    id: 'senior-dep',
    name: 'Senior Citizen Deposit',
    tagline: 'Safeguard your retirement payouts with a special 0.50% interest rate premium',
    rate: 'Up to 8.75% p.a.',
    numericRate: 8.75,
    tenure: '1 Year to 5 Years',
    minDeposit: '₹5,000',
    numericMin: 5000,
    maxDeposit: 'No Limit',
    features: [
      'Additional 0.50% interest rate payout over standard public slabs.',
      'Flexible monthly cash-out logs directly fund everyday retirement expenses.',
      'Hassle-free Form 15H submission support to exempt TDS calculations.',
      'Priority service desk preference at all 12+ branches.'
    ],
    benefits: [
      'Higher interest yield: Earn up to 8.75% p.a. to fight inflation.',
      'Income support: Setup automated monthly ledger interest payouts.',
      'Exemptions: Higher TDS thresholds up to ₹50,000 of interest p.a.'
    ],
    overview: 'We offer specialized senior deposit accounts for co-members aged 60 and above. Recognizing the importance of post-retirement security, this scheme provides our highest compound yields combined with priority support desks and simple TDS tax exemptions.',
    eligibility: [
      'Indian citizen residents aged 60 years or above on deposit registration date.',
      'Joint accounts where the primary holder meets the age limit.'
    ],
    faqs: [
      { q: 'Is proof of age mandatory?', a: 'Yes, PAN card, Aadhaar card, or Birth Certificate is required.' },
      { q: 'Can interest be paid directly to a different bank?', a: 'We support automated monthly transfers to any registered Sahyadri savings ledger.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  },
  {
    id: 'women-dep',
    name: 'Women Savings Scheme',
    tagline: 'Dedicated savings yields and free digital tools to promote financial independence',
    rate: '4.25% p.a. (Savings)',
    numericRate: 4.25,
    tenure: 'Continuous',
    minDeposit: '₹200',
    numericMin: 200,
    maxDeposit: 'No Limit',
    features: [
      'Exclusive higher savings rate of 4.25% p.a. on savings balances.',
      'Zero balance maintenance charges and no hidden transactional costs.',
      'Priority access to financial literacy programs hosted by the society.',
      'Free secure mobile access passbook and online transfer modules.'
    ],
    benefits: [
      'Feminine empowerment: Higher yields for cooperative women members.',
      'Zero thresholds: No monthly fines for lower balances.',
      'Support network: Connect with community credit representatives.'
    ],
    overview: 'The Sahyadri Women Savings Scheme is crafted to support the financial growth of women. It provides premium savings yields, zero maintenance penalties, and direct guidance on compound investments. We aim to encourage micro-savings and independent asset building.',
    eligibility: [
      'Sole or joint accounts held by women residents aged 18 and above.',
      'Girl minor accounts registered under maternal guardianship.'
    ],
    faqs: [
      { q: 'Are there checkbook facilities?', a: 'Yes, free checkbooks are provided upon request with standard KYC verification.' },
      { q: 'Is there a limit on cash withdrawals?', a: 'Standard daily card limits apply; branch cash counters offer unlimited liquid service.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  },
  {
    id: 'double-benefit',
    name: 'Double Benefit Deposit',
    tagline: 'Invest a lump sum and guarantee a secure 2x maturity value in 100 months',
    rate: 'Guaranteed 2x Payout',
    numericRate: 8.50,
    tenure: 'approx. 100 Months',
    minDeposit: '₹10,000',
    numericMin: 10000,
    maxDeposit: 'No Limit',
    features: [
      'Principal doubles safely in approximately 100 months.',
      'Quarterly compound interest reinvested continuously into the base.',
      'Receipt certificates printed instantly at registry desks.',
      'Excellent long-term tool to fund higher studies or property purchases.'
    ],
    benefits: [
      'Guaranteed return: Clear doubling multiplier, zero market risk.',
      'Collateral ready: Eligible for instant loans up to 90% of principal.',
      'Automatic renewal: Option to auto-reinvest upon doubling maturity.'
    ],
    overview: 'Our Double Benefit Deposit serves as an ideal wealth multiplier. By leaving your principal locked for a fixed term, interest compounds continuously to double your initial sum in roughly 100 months. This guaranteed payout offers complete peace of mind with zero risk.',
    eligibility: [
      'Indian residents, joint co-members, and local organizations.',
      'Minor accounts managed under parents or guardians.'
    ],
    faqs: [
      { q: 'How is the doubling period calculated?', a: 'It depends on prevailing compounding rates, generally settling near 100 months.' },
      { q: 'Is the maturity certificate acceptable at other banks?', a: 'The certificate is verified and valid for loans inside our society network.' }
    ],
    documents: [
      'Aadhaar Card UID',
      'PAN Card Number',
      'Recent Utility Bill',
      '2 Passport Photos'
    ],
    interestPayouts: 'Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity.',
    prematureWithdrawal: 'Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs.',
    loanMargin: 'Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance.',
    nominationFacility: 'Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration.',
    terms: [
      'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
      'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
      'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
      'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
    ]
  }
];

export default function Deposits() {
  const location = useLocation();
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [calcPrincipal, setCalcPrincipal] = useState(100000);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: '',
    amount: '',
    tenure: '',
    schemeType: 'Savings Deposit Account'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'deposit_enquiries'), {
        ...formData,
        status: 'New',
        createdAt: new Date().toISOString()
      });
    } catch (err) {
      console.warn('Could not save enquiry to Firestore:', err);
    }
    alert(`Thank you, ${formData.name}! Your application for the ${formData.schemeType} of amount ₹${parseFloat(formData.amount).toLocaleString('en-IN')} has been submitted. Our executive will reach out within 24 hours.`);
    setIsModalOpen(false);
    setFormData({
      name: '',
      contact: '',
      email: '',
      city: '',
      amount: '',
      tenure: '',
      schemeType: 'Savings Deposit Account'
    });
  };

  const handleDownload = (docItem) => {
    if (docItem.url) {
      window.open(docItem.url, '_blank');
    } else {
      alert(`Downloading ${docItem.name}... (Sample PDF file simulation)`);
    }
  };

  const [calcTenure, setCalcTenure] = useState(3); // in years/months depending on scheme
  const [faqOpen, setFaqOpen] = useState({});

  // Handle Hash routing from Navbar dropdowns reactively using React Router location and dynamic schemes
  const [activeSchemes, setActiveSchemes] = useState(defaultDepositSchemes);

  useEffect(() => {
    const fetchCustomSchemes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'deposit_schemes'));
        const fbSchemes = [];
        querySnapshot.forEach((doc) => {
          fbSchemes.push({ id: doc.id, ...doc.data() });
        });

        const merged = [...defaultDepositSchemes];
        if (fbSchemes.length > 0) {
          fbSchemes.forEach((fbS) => {
            const idx = merged.findIndex(s => s.id === fbS.id);
            if (idx > -1) {
              merged[idx] = fbS;
            } else {
              merged.push(fbS);
            }
          });
        }
        setActiveSchemes(merged);
      } catch (err) {
        console.warn("Could not load dynamic schemes from Firestore:", err);
        setActiveSchemes(defaultDepositSchemes);
      }
    };
    fetchCustomSchemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const found = activeSchemes.find(s => s.id === id);
      if (found) {
        setSelectedScheme(found);
        // Prefill calculator with scheme details
        setCalcPrincipal(found.numericMin);
      } else {
        setSelectedScheme(null);
      }
    } else {
      setSelectedScheme(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.pathname, activeSchemes]);

  const selectScheme = (scheme) => {
    setSelectedScheme(scheme);
    setCalcPrincipal(scheme.numericMin);
    window.location.hash = `#${scheme.id}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSelection = () => {
    setSelectedScheme(null);
    window.location.hash = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToCalculator = () => {
    const calcSec = document.getElementById('maturity-calculator');
    if (calcSec) {
      calcSec.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (idx) => {
    setFaqOpen(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Maturity calculation for calculator inside detail page
  const calculateDetailsReturn = () => {
    const P = calcPrincipal;
    const r = (selectedScheme?.numericRate || 7.5) / 100;
    const t = calcTenure;
    let maturity = 0;

    if (selectedScheme?.id === 'savings-dep' || selectedScheme?.id === 'women-dep') {
      // simple interest for liquid savings
      maturity = P * (1 + r * t);
    } else {
      // compound quarterly
      maturity = P * Math.pow(1 + r / 4, 4 * t);
    }

    const interest = maturity - P;
    return {
      maturity: Math.round(maturity),
      interest: Math.round(interest)
    };
  };

  const results = calculateDetailsReturn();

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Dynamic Detail Page View */}
      {selectedScheme ? (
        <div className="animate-fadeIn pb-24">
          {/* Back Button Panel */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button
              onClick={clearSelection}
              className="inline-flex items-center space-x-2 text-xs font-bold text-[#B31825] uppercase tracking-wider hover:underline bg-white border border-gray-200 px-4 py-2 rounded-xl shadow-sm transition-all"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Deposits Portal</span>
            </button>
          </div>

          {/* 1. HERO SECTION */}
          <section className="bg-gradient-to-r from-navy to-navy-dark text-white py-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(255,255,255,0.03),transparent)] pointer-events-none" />
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">

              {/* Text column */}
              <div className="lg:col-span-7 space-y-6">
                <span className="text-xs font-bold text-golden uppercase tracking-widest bg-white/10 px-3.5 py-1.5 rounded-full w-fit block">
                  {selectedScheme.rate} Yield
                </span>
                <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                  {selectedScheme.name}
                </h1>
                <p className="text-gray-300 text-sm sm:text-base font-light leading-relaxed max-w-xl">
                  {selectedScheme.tagline}
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, schemeType: selectedScheme.name }));
                      setIsModalOpen(true);
                    }}
                    className="px-6 py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg shadow-[#B31825]/20 font-sans"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={scrollToCalculator}
                    className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/15"
                  >
                    Calculate Returns
                  </button>
                </div>
              </div>

              {/* Graphic / Highlight block */}
              <div className="lg:col-span-5 bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 backdrop-blur-sm space-y-6">
                <h3 className="font-montserrat font-bold text-sm text-golden uppercase tracking-widest">Quick Highlights</h3>
                <div className="grid grid-cols-2 gap-6 text-xs font-sans">
                  <div className="bg-[#020c1a]/55 border border-white/5 p-4 rounded-2xl">
                    <span className="text-gray-400 block font-bold uppercase tracking-wider text-[9px] mb-1">Interest rate</span>
                    <span className="text-white font-extrabold text-sm block font-mono">{selectedScheme.rate}</span>
                  </div>
                  <div className="bg-[#020c1a]/55 border border-white/5 p-4 rounded-2xl">
                    <span className="text-gray-400 block font-bold uppercase tracking-wider text-[9px] mb-1">Min. Deposit</span>
                    <span className="text-white font-extrabold text-sm block font-mono">{selectedScheme.minDeposit}</span>
                  </div>
                  <div className="bg-[#020c1a]/55 border border-white/5 p-4 rounded-2xl">
                    <span className="text-gray-400 block font-bold uppercase tracking-wider text-[9px] mb-1">Max. Deposit</span>
                    <span className="text-white font-extrabold text-sm block font-mono">{selectedScheme.maxDeposit}</span>
                  </div>
                  <div className="bg-[#020c1a]/55 border border-white/5 p-4 rounded-2xl">
                    <span className="text-gray-400 block font-bold uppercase tracking-wider text-[9px] mb-1">Tenure</span>
                    <span className="text-white font-extrabold text-sm block font-mono">{selectedScheme.tenure}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 2. SCHEME OVERVIEW */}
          <section className="py-24 bg-white text-navy font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block">Overview</span>
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">About the Scheme</h2>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  {selectedScheme.overview}
                </p>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  Our cooperative society operates under strict audit guidelines to ensure complete protection for member capital. By selecting our plans, you secure guaranteed earnings while supporting agricultural and rural microcredit projects across Sangli and larger Maharashtra hubs.
                </p>
              </div>

              {/* Image Section replacing Visual Frame */}
              <div
                onClick={() => setShowLightbox(true)}
                className="bg-white border border-gray-250 rounded-3xl overflow-hidden flex items-center justify-center aspect-video shadow-md cursor-pointer hover:shadow-lg hover:border-[#B31825] transition-all group"
              >
                <img
                  src={selectedScheme.imageUrl || "/deposit sch.png"}
                  alt="Deposit Scheme Banner"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </div>
          </section>

          {/* 3. KEY HIGHLIGHTS, BENEFITS & FEATURES */}
          <section className="py-24 bg-neutralBg text-navy border-t border-gray-250">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-[#B31825] font-montserrat font-bold text-xs uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Highlights</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Product Features & Benefits</h2>
                <p className="text-gray-500 text-sm">Key guidelines and added advantages designed for member satisfaction.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Feature cards */}
                {selectedScheme.features.map((feat, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 hover:border-[#B31825] hover:shadow-xl transition-all duration-300 space-y-4">
                    <div className="p-3 bg-emerald/10 text-emerald rounded-2xl w-fit">
                      <CheckCircle2 className="h-5 w-5 text-emerald" />
                    </div>
                    <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">{feat}</p>
                  </div>
                ))}

                {/* Additional Benefit cards */}
                {selectedScheme.benefits.map((ben, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 hover:border-[#052046] hover:shadow-xl transition-all duration-300 space-y-4">
                    <div className="p-3 bg-primary/10 text-primary rounded-2xl w-fit">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">{ben}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 4. ELIGIBILITY & DOCUMENTS */}
          <section className="py-24 bg-white text-navy border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

              {/* Eligibility card */}
              <div className="bg-[#F8FAFC] border border-gray-250 rounded-3xl p-8 sm:p-10 shadow-sm space-y-6">
                <h3 className="font-montserrat font-extrabold text-lg text-navy flex items-center">
                  <UserCheck className="h-5.5 w-5.5 mr-3 text-[#B31825]" />
                  <span>Eligibility Criteria</span>
                </h3>
                <ul className="space-y-4 text-xs sm:text-sm text-gray-500 font-light">
                  {selectedScheme.eligibility.map((elig, idx) => (
                    <li key={idx} className="flex items-start bg-white p-3.5 rounded-xl border border-gray-150">
                      <span className="text-emerald mr-3 font-bold select-none">✓</span>
                      <span>{elig}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Documents card */}
              <div className="bg-[#F8FAFC] border border-gray-250 rounded-3xl p-8 sm:p-10 shadow-sm space-y-6">
                <h3 className="font-montserrat font-extrabold text-lg text-navy flex items-center">
                  <FileText className="h-5.5 w-5.5 mr-3 text-primary" />
                  <span>Required Documents</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-500 font-light font-poppins">
                  {(selectedScheme.documents && selectedScheme.documents.length > 0
                    ? selectedScheme.documents
                    : ['Aadhaar Card UID', 'PAN Card Number', 'Recent Utility Bill', '2 Passport Photos']
                  ).map((docText, idx) => (
                    <div key={idx} className="flex items-center space-x-3 bg-white p-3 rounded-xl border border-gray-150">
                      <CheckCircle2 className="h-4 w-4 text-[#B31825]" />
                      <span>{docText}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10">
                  <p className="text-[10px] text-gray-400 font-light leading-normal">
                    *Note: Original physical documents must be presented at the verification desk along with self-attested photocopies for immediate ledger submission.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. EXTRA BANKING DETAILS (Tenure, Payouts, Premature, Loan) */}
          <section className="py-24 bg-neutralBg text-navy border-t border-gray-250">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Payout Options */}
              <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-[#B31825] transition-all">
                <Clock className="h-5 w-5 text-[#B31825] mb-4" />
                <h4 className="font-montserrat font-extrabold text-sm text-navy mb-2">Interest Payouts</h4>
                <p className="text-gray-500 text-xs leading-relaxed font-light font-poppins">
                  {selectedScheme.interestPayouts || "Choose between monthly direct credits, quarterly compounds, or a lump-sum payout at maturity."}
                </p>
              </div>

              {/* Premature Closure */}
              <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-[#B31825] transition-all">
                <Info className="h-5 w-5 text-[#B31825] mb-4" />
                <h4 className="font-montserrat font-extrabold text-sm text-navy mb-2">Premature Withdrawal</h4>
                <p className="text-gray-500 text-xs leading-relaxed font-light font-poppins">
                  {selectedScheme.prematureWithdrawal || "Premature closure is permitted. A nominal fee penalty of 1.00% is adjusted against current interest slabs."}
                </p>
              </div>

              {/* Loan Against Deposit */}
              <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-[#B31825] transition-all">
                <Key className="h-5 w-5 text-[#B31825] mb-4" />
                <h4 className="font-montserrat font-extrabold text-sm text-navy mb-2">Loan Margin</h4>
                <p className="text-gray-500 text-xs leading-relaxed font-light font-poppins">
                  {selectedScheme.loanMargin || "Avail overdraft credit lines or loans against your deposit receipt up to 90% of accumulated balance."}
                </p>
              </div>

              {/* Nomination */}
              <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-[#B31825] transition-all">
                <ShieldCheck className="h-5 w-5 text-[#B31825] mb-4" />
                <h4 className="font-montserrat font-extrabold text-sm text-navy mb-2">Nomination Facility</h4>
                <p className="text-gray-500 text-xs leading-relaxed font-light font-poppins">
                  {selectedScheme.nominationFacility || "Nominate up to two legal beneficiaries under simple sign-off mandate forms during registration."}
                </p>
              </div>
            </div>
          </section>

          {/* 6. SCHEME CALCULATOR */}
          <section id="maturity-calculator" className="py-24 bg-white text-navy border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Calculator</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Maturity Return Planner</h2>
                <p className="text-gray-500 text-sm">Calculate your compound growth payout for {selectedScheme.name} immediately.</p>
              </div>

              <div className="bg-[#052046] border border-white/10 rounded-3xl p-6 sm:p-10 text-white grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">

                {/* Left - Sliders */}
                <div className="lg:col-span-7 space-y-6 pr-0 lg:pr-6">
                  {/* Amount Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-300 font-bold uppercase tracking-wider text-[10px]">Investment Size</span>
                      <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{calcPrincipal.toLocaleString('en-IN')}</span>
                    </div>
                    <input
                      type="range"
                      min={selectedScheme.numericMin}
                      max="5000000"
                      step="5000"
                      value={calcPrincipal}
                      onChange={(e) => setCalcPrincipal(Number(e.target.value))}
                      className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                      <span>Min: {selectedScheme.minDeposit}</span>
                      <span>Max: ₹50 Lakhs</span>
                    </div>
                  </div>

                  {/* Tenure Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-300 font-bold uppercase tracking-wider text-[10px]">Investment Tenure</span>
                      <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">
                        {calcTenure} {selectedScheme.id === 'savings-dep' || selectedScheme.id === 'women-dep' ? 'Years' : 'Years'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={calcTenure}
                      onChange={(e) => setCalcTenure(Number(e.target.value))}
                      className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                      <span>1 Year</span>
                      <span>10 Years</span>
                    </div>
                  </div>
                </div>

                {/* Right - Calculations Display */}
                <div className="lg:col-span-5 bg-gradient-to-br from-[#03142c] to-[#010915] border border-white/10 rounded-3xl p-6 sm:p-8 flex flex-col justify-between space-y-6 shadow-xl">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 block font-bold">Maturity Payout</span>
                    <span className="font-montserrat font-extrabold text-3xl text-emerald block mt-2 font-mono">
                      ₹{results.maturity.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-6 font-sans">
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Principal Invested</span>
                      <span className="text-sm font-bold text-white block mt-1 font-mono">₹{calcPrincipal.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Interest Earned</span>
                      <span className="text-sm font-bold text-golden block mt-1 font-mono">₹{results.interest.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* 7. DOWNLOAD CENTER */}
          <section className="py-24 bg-neutralBg text-navy border-t border-gray-250">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-2">
                <span className="text-[#B31825] font-montserrat font-bold text-xs uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Downloads</span>
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Form & Brochure Download Center</h2>
                <p className="text-gray-500 text-sm">Access official cooperative mandate copies and scheme descriptions.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {[
                  ...(selectedScheme.brochures && selectedScheme.brochures.length > 0 ? selectedScheme.brochures.map(b => ({
                    name: b.title || `${selectedScheme.name} Brochure`,
                    desc: b.desc || `Official details, terms, and guidelines for the ${selectedScheme.name}.`,
                    url: b.pdfUrl
                  })) : (selectedScheme.pdfUrl ? [{
                    name: selectedScheme.pdfTitle || `${selectedScheme.name} Brochure`,
                    desc: selectedScheme.pdfDesc || `Official details, terms, and guidelines for the ${selectedScheme.name}.`,
                    url: selectedScheme.pdfUrl
                  }] : [])),
                  { name: 'Membership Application Form', desc: 'Required for onboarding savings or term accounts.' },
                  { name: 'Interest Rate Slabs Guide', desc: 'Direct reference booklet of prevailing co-operative deposit percentages.' },
                  { name: 'Premature Closure Application', desc: 'Mandate copy to initiate emergency liquidation funds.' }
                ].map((docItem, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-3xl p-6 flex flex-col justify-between hover:border-[#B31825] hover:shadow-lg transition-all duration-300">
                    <div className="space-y-2">
                      <h4 className="font-montserrat font-extrabold text-sm text-navy">{docItem.name}</h4>
                      <p className="text-gray-400 text-xs font-light leading-relaxed">{docItem.desc}</p>
                    </div>
                    <button
                      onClick={() => handleDownload(docItem)}
                      className="inline-flex items-center space-x-2 text-xs font-bold text-[#B31825] uppercase tracking-wider hover:underline pt-4 mt-4 border-t border-gray-100 w-full text-left"
                    >
                      <Download className="h-4 w-4 shrink-0" />
                      <span>Download PDF</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 8. FAQs & TERMS ACCORDIONS */}
          <section className="py-24 bg-white text-navy border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-4 space-y-12">
              <div className="text-center space-y-2">
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Frequently Asked Questions</h2>
                <p className="text-gray-500 text-xs sm:text-sm">Find quick answers to common queries regarding {selectedScheme.name}.</p>
              </div>

              <div className="space-y-4">
                {selectedScheme.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-[#F8FAFC] border border-gray-200 rounded-2xl overflow-hidden transition-colors">
                    <button
                      onClick={() => toggleFaq(idx)}
                      className="w-full flex justify-between items-center p-5 text-left font-montserrat font-bold text-sm text-navy hover:text-[#B31825] transition-colors"
                    >
                      <span>{faq.q}</span>
                      <span className="text-xs text-gray-400 font-bold font-mono">{faqOpen[idx] ? '—' : '+'}</span>
                    </button>
                    {faqOpen[idx] && (
                      <div className="p-5 border-t border-gray-150 text-xs sm:text-sm text-gray-500 leading-relaxed font-light">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* T&C Scroll block */}
              <div className="bg-[#F8FAFC] border border-gray-250 rounded-3xl p-6 sm:p-8 space-y-4">
                <h4 className="font-montserrat font-extrabold text-sm text-[#052046] flex items-center">
                  <Info className="h-4.5 w-4.5 mr-2 text-[#B31825]" />
                  <span>Important Terms & Conditions</span>
                </h4>
                <div className="max-h-40 overflow-y-auto pr-2 no-scrollbar text-xs text-gray-400 leading-relaxed font-light space-y-2 font-poppins">
                  {(selectedScheme.terms && selectedScheme.terms.length > 0
                    ? selectedScheme.terms
                    : [
                        'Interest rates are calculated under co-operative board guidelines and are subject to change without notice.',
                        'Senior Citizen premium is strictly applicable only to accounts where the primary holder is aged 60 and above.',
                        'Premature withdrawals attract a penalty of 1.00% adjusted on the interest rate applicable for the tenure during which the deposit remained locked.',
                        'TDS guidelines apply as per Central Income Tax mandates. Please submit Form 15G/15H annually to request tax exemptions.'
                      ]
                  ).map((termText, idx) => (
                    <p key={idx}>• {termText}</p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 9. FINAL CALL TO ACTION (CTA) */}
          <section className="bg-gradient-to-r from-navy to-navy-dark text-white py-16 px-4 text-center border-t border-white/5 relative">
            <div className="max-w-4xl mx-auto space-y-6 relative z-10">
              <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl">Start Earning Compounding Returns Today</h2>
              <p className="text-gray-300 text-xs sm:text-sm font-light max-w-md mx-auto">
                Opening an account is simple. Onboard online in minutes or talk to an advisor at your nearest Sahyadri branch.
              </p>
              <div className="flex flex-wrap justify-center gap-4 pt-2 select-none">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, schemeType: selectedScheme.name }));
                    setIsModalOpen(true);
                  }}
                  className="px-8 py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all font-sans"
                >
                  Apply for Deposit
                </button>
                <Link
                  to="/contact"
                  className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/15"
                >
                  Contact Branch
                </Link>
              </div>
            </div>
          </section>

          {/* 10. STICKY ACTION BAR */}
          <div className="fixed bottom-0 left-0 w-full z-45 bg-[#052046]/95 backdrop-blur-md border-t border-white/10 py-3.5 px-4 shadow-xl select-none">
            <div className="max-w-7xl mx-auto flex justify-between items-center gap-3">
              <span className="hidden sm:inline font-montserrat font-bold text-xs text-white tracking-wide">
                Investing in {selectedScheme.name}?
              </span>
              <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, schemeType: selectedScheme.name }));
                    setIsModalOpen(true);
                  }}
                  className="px-4 py-2 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all font-sans"
                >
                  Apply
                </button>
                <button
                  onClick={scrollToCalculator}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all border border-white/15"
                >
                  Calculate
                </button>
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 bg-white text-navy font-bold rounded-lg text-[10px] sm:text-xs uppercase tracking-wider transition-all hover:bg-gray-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

        </div>
      ) : (
        /* Original list/grid portal view */
        <div>
          {/* Page Header */}
          <section
            className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/page header.png')" }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
            <div className="max-w-4xl mx-auto space-y-4 relative z-10">
              <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Investment Portfolios</span>
              <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Sahyadri High-Yield Deposit Schemes</h1>
              <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
                Secure your idle assets, earn compounding interest rates, and build collateral for instant personal lines of credit.
              </p>
            </div>
          </section>

          {/* Schemes Grid */}
          <section className="py-24 bg-white text-navy">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Available Schemes</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Tailored Term & Savings Portfolios</h2>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed">
                  We provide high interest yields compared to standard banks, secured under co-operative audit criteria.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {activeSchemes.map((scheme, i) => (
                  <div
                    key={i}
                    id={scheme.id}
                    className="bg-[#F8FAFC] border border-gray-200/80 rounded-3xl p-6 sm:p-8 hover:bg-white hover:border-[#B31825] hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group"
                  >
                    <div className="space-y-5">
                      <div className="flex justify-between items-start gap-3">
                        <h3 className="font-montserrat font-extrabold text-lg text-navy leading-snug group-hover:text-[#B31825] transition-colors">{scheme.name}</h3>
                        <span className="bg-emerald/10 text-emerald text-xs font-bold px-3 py-1 rounded-full shrink-0">
                          {scheme.rate}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-xs border-y border-gray-150 py-4 font-poppins">
                        <div>
                          <span className="text-gray-400 block font-bold uppercase tracking-wider text-[10px]">Tenure</span>
                          <span className="text-gray-800 font-bold block mt-0.5">{scheme.tenure}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 block font-bold uppercase tracking-wider text-[10px]">Min. Deposit</span>
                          <span className="text-gray-800 font-bold block mt-0.5">{scheme.minDeposit}</span>
                        </div>
                      </div>

                      <ul className="space-y-3 text-xs sm:text-sm text-gray-500 pt-2 font-light">
                        {scheme.features.map((feat, fIdx) => (
                          <li key={fIdx} className="flex items-start">
                            <CheckCircle2 className="h-4 w-4 text-[#B31825] shrink-0 mr-3 mt-0.5" />
                            <span>{feat.split(':')[0]}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-8 grid grid-cols-2 gap-3">
                      <button
                        onClick={() => selectScheme(scheme)}
                        className="text-center block py-3 bg-[#052046] hover:bg-[#0c3164] text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, schemeType: scheme.name }));
                          setIsModalOpen(true);
                        }}
                        className="text-center block py-3 bg-white border border-[#B31825] text-[#B31825] hover:bg-[#B31825] hover:text-white rounded-xl text-xs font-bold transition-all shadow-sm font-sans"
                      >
                        Apply Online
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Dynamic calculator center */}
          <section className="py-24 bg-neutralBg text-navy border-t border-gray-250">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
                <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit mx-auto block">Savings Planner</span>
                <h2 className="font-montserrat font-extrabold text-3xl text-navy">Maturity Interest Calculator</h2>
                <p className="text-gray-600 text-sm sm:text-base leading-relaxed font-light">
                  Input your investment size and term below to calculate compounding payouts immediately.
                </p>
              </div>

              <Calculator />
            </div>
          </section>

          {/* FD Interest Rates Table summary */}
          <section className="py-24 bg-white text-navy border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-4 space-y-8">
              <div className="text-center space-y-2">
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy flex items-center justify-center gap-2">
                  <Table className="h-6 w-6 text-[#B31825]" />
                  <span>Term Deposit Interest Rates Structure</span>
                </h2>
                <p className="text-gray-500 text-xs sm:text-sm">Effective from July 1st, 2026. Subject to change as per Board directives.</p>
              </div>

              <div className="bg-[#F8FAFC] border border-gray-200 rounded-3xl overflow-hidden shadow-lg">
                <table className="w-full text-left text-xs sm:text-sm font-sans">
                  <thead className="bg-[#052046] text-white font-montserrat uppercase tracking-wider text-[10px] sm:text-xs">
                    <tr>
                      <th className="px-6 sm:px-8 py-5 font-bold">Tenure Range</th>
                      <th className="px-6 sm:px-8 py-5 font-bold">General Public (p.a.)</th>
                      <th className="px-6 sm:px-8 py-5 font-bold">Senior Citizens (p.a.)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-150 text-gray-700 font-poppins">
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">7 Days to 90 Days</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">5.00%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">5.50%</td>
                    </tr>
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">91 Days to 180 Days</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">5.75%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">6.25%</td>
                    </tr>
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">181 Days to 1 Year</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">6.25%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">6.75%</td>
                    </tr>
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">1 Year to 2 Years</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">7.25%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">7.75%</td>
                    </tr>
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">2 Years to 3 Years</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">7.75%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">8.25%</td>
                    </tr>
                    <tr className="hover:bg-white transition-colors">
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-navy">3 Years to 10 Years</td>
                      <td className="px-6 sm:px-8 py-4.5 font-medium">8.25%</td>
                      <td className="px-6 sm:px-8 py-4.5 font-bold text-emerald">8.75%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* APPLICATION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 relative animate-fadeIn">

            {/* Modal Header */}
            <div className="bg-[#052046] text-white p-6 relative">
              <h3 className="font-montserrat font-extrabold text-lg text-white">Apply for Deposit Scheme</h3>
              <p className="text-xs text-gray-300 mt-1 font-light">Fill out your deposit details to start onboarding.</p>
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4 text-navy">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Contact Number</label>
                <input
                  type="tel"
                  name="contact"
                  required
                  placeholder="Enter 10-digit mobile"
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">City / Location</label>
                <input
                  type="text"
                  name="city"
                  required
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#052046] uppercase">Deposit Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    required
                    placeholder="e.g. 50000"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#052046] uppercase">Tenure (Months/Yrs)</label>
                  <input
                    type="text"
                    name="tenure"
                    required
                    placeholder="e.g. 3 Years"
                    value={formData.tenure}
                    onChange={handleInputChange}
                    className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Deposit Scheme</label>
                <select
                  name="schemeType"
                  value={formData.schemeType}
                  onChange={handleInputChange}
                  className="w-full bg-[#F8FAFC] border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2 text-sm outline-none transition-all"
                >
                  <option value="Savings Deposit Account">Savings Deposit Account</option>
                  <option value="Fixed Deposit (FD)">Fixed Deposit (FD)</option>
                  <option value="Recurring Deposit (RD)">Recurring Deposit (RD)</option>
                  <option value="Senior Citizen Deposit">Senior Citizen Deposit</option>
                  <option value="Women Savings Scheme">Women Savings Scheme</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {showLightbox && (
        <div
          onClick={() => setShowLightbox(false)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white p-2 shadow-2xl transition-transform duration-300 scale-100 hover:scale-102">
            <button
              onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
              className="absolute top-4 right-4 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={selectedScheme.imageUrl || "/deposit sch.png"}
              alt="Deposit Scheme Banner Zoomed"
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
